import React, {useEffect, useState, Fragment} from "react";
import {deletedNewsletterTypes, OptionsForm, SuccessMessageBlock} from "../../../newsletter/NewsLetterOptionsForm";
import {CustomerFactory, INewsletter} from "../../../../typescript-library";
import {getShopId} from "../../../lib/getShopId";
import {useTranslation, Trans} from "react-i18next";
import Spinner from "../../../utils/loader/Spinner";
import Modal from "../../../lib/ModalWindow/Modal";
import NoNewsletterSubscription from "./NoNewsletterSubscription";
import styles from "./NewsletterView.module.scss";
import {useCustomerSelector} from "../../../../store";

const NewsletterView: React.FC = () => {
  const {customer} = useCustomerSelector();
  const {t} = useTranslation();
  const [loadingState, setLoadingState] = useState<{update?: boolean; subscribe?: boolean}>({
    update: false,
    subscribe: false,
  });
  const [cancelSubscribtionModalVisible, setCancelSubscribtionModalVisible] = useState(false);
  const [birthday, setBirthday] = useState<string | undefined>();
  const [customerEmail, setCustomerEmail] = useState<string>();
  const [successState, setSuccessState] =
    useState<{
      updateSubscribe?: boolean;
      firstSubscribe?: boolean;
      cancelSubscribe?: boolean;
    }>();
  const [nlOptionsState, setNlOptionsState] = useState<INewsletter[]>([]);

  useEffect(() => {
    void customer.getNewsletterOptions(getShopId()).then((data) => {
      setNlOptionsState(data.filter((option) => !deletedNewsletterTypes.includes(option.newsletterTypeId)));
    });
    const boxContent = document.querySelectorAll<HTMLDivElement>(".boxContent");
    boxContent.forEach((el) => (el.style.display = "none"));

    return () => boxContent.forEach((el) => (el.style.display = ""));
  }, []);

  const saveNewsletterSettings = () => {
    if (nlOptionsState.find((option) => option.newsletterTypeId === 100009)?.isSubscribed && !birthday) {
      setBirthday(undefined);

      return;
    }

    if (loadingState.update) {
      return;
    }

    setLoadingState({update: true});
    void customer
      .setNewsletterOptions(getShopId(), nlOptionsState)
      .then(() => {
        setLoadingState({update: false});
        setSuccessState({updateSubscribe: true});
        window.scrollTo({top: 0, left: 0, behavior: "smooth"});
      })
      .then(() => {
        if (nlOptionsState.find((option) => option.newsletterTypeId === 100009)?.isSubscribed) {
          void customer.putProperties(getShopId(), {
            birthday,
          });
        }
      });
  };

  const toggleSubscribeNewsletter = async (newsletterFlag: boolean, email: string) => {
    if (loadingState.subscribe) {
      return;
    }
    setLoadingState({subscribe: true});

    await CustomerFactory.createOrUpdateCustomer(getShopId(), {
      email,
      newsletterFlag,
    });

    if (newsletterFlag) {
      setSuccessState({firstSubscribe: true});
    } else {
      setSuccessState({cancelSubscribe: true});
      setCancelSubscribtionModalVisible(false);
    }
    setLoadingState({subscribe: false});
    window.scrollTo({top: 0, left: 0, behavior: "smooth"});
  };

  const CancelSubscribtionForm = () => {
    return (
      <Fragment>
        <SuccessMessageBlock>{t("newsletter.unsubscribedSuccessfully")}</SuccessMessageBlock>
        <p>{t("newsletter.subscribeAnyTimeAgain")}</p>
        <div className={styles.buttonWrapper}>
          <a className={styles.submitBtn} href={`${t("url.userAccountLink")}?view=subscriptions`}>
            <span>{t("newsletter.subscribeNewsletter")}</span>
          </a>
        </div>
      </Fragment>
    );
  };

  return (
    <Fragment>
      {successState?.firstSubscribe && (
        <SuccessMessageBlock>
          <Trans i18nKey="newsletter.confirmationMail--customerEmail"
            ns="translations"
            values={{customerEmail}}
            components={{
              b: <b/>,
              s: <span className={`${styles.link} ${loadingState.subscribe ? styles.disabled : ""}`} onClick={() => {setSuccessState({firstSubscribe: false});}}/>
            }}
          />
        </SuccessMessageBlock>
      )}
      {!customer.newsletterFlag && !successState?.firstSubscribe && !successState?.cancelSubscribe && (
        <NoNewsletterSubscription
          customerMail={customerEmail}
          onSubmit={(email) => {
            setCustomerEmail(email);
            void toggleSubscribeNewsletter(true, email);
          }}
          isLoading={!!loadingState.subscribe}
        />
      )}
      {successState?.cancelSubscribe && <CancelSubscribtionForm />}
      {customer.newsletterFlag && !successState?.cancelSubscribe && (
        <React.Fragment>
          <h2 className={"title"}>{t("newsletter.newsletterOptions")}</h2>
          {successState?.updateSubscribe && (
            <SuccessMessageBlock>
              {t("newsletter.settingsUpdated")}
            </SuccessMessageBlock>
          )}
          <OptionsForm
            nlOptions={nlOptionsState}
            onChange={(options) => {
              setNlOptionsState(options);
            }}
            isBirthdayRequired={birthday === undefined}
            showBirthdayInput={nlOptionsState.some(
              (option) => option.newsletterTypeId === 100009 && option.isSubscribed,
            )}
            onChangeBirthday={(data) => {
              setBirthday(data);
            }}
          />

          <div className={styles.buttonWrapper}>
            <button
              className={styles.submitBtn}
              disabled={nlOptionsState.every((option) => !option.isSubscribed)}
              onClick={() => {
                saveNewsletterSettings();
              }}
            >
              {loadingState.update ? <Spinner /> : <span>{t("newsletter.saveSettings")}</span>}
            </button>
          </div>
          <div className={styles.cancelSection}>
            <h3>{t("newsletter.unsubscribe")}</h3>
            <p>{t("newsletter.unsubscribeText")}</p>

            <button
              className={`${styles.submitBtn} ${styles.cancel}`}
              onClick={() => {
                setCancelSubscribtionModalVisible(true);
              }}
            >
              {t("newsletter.unsubscribeAllNewsletter")}
            </button>
          </div>
          <Modal
            isVisible={cancelSubscribtionModalVisible}
            closeFunction={() => {
              setCancelSubscribtionModalVisible(false);
            }}
          >
            <h3 className={styles.modalTitle}>{t("general.areYouSure")}</h3>
            <p>
              {t(
                "newsletter.unsubscribeModalText",
              )}
            </p>

            <div className={styles.modalButtonsWrapper}>
              <button
                className={`${styles.submitBtn} ${styles.cancel} ${styles.buttonModal}`}
                onClick={() => {
                  setCancelSubscribtionModalVisible(false);
                }}
              >
                {t("general.noBack")}
              </button>
              <button
                className={`${styles.submitBtn} ${styles.buttonModal}`}
                onClick={() => {
                  void toggleSubscribeNewsletter(false, customer.email || "");
                }}
              >
                {loadingState.subscribe ? <Spinner /> : <span>{t("newsletter.unsubscribeConfirmation")}</span>}
              </button>
            </div>
          </Modal>
        </React.Fragment>
      )}
    </Fragment>
  );
};

export default NewsletterView;
